// Copyright 1999-2026. WebPros International GmbH. All rights reserved.

import { ConsoleOutput, Hint, SegmentedControl, Text, useTranslate } from '@plesk/ui-library';
import { useMemo, useState } from 'react';
import type { ToolCall } from '../../../CopilotContext';
import type { ToolCallPayload } from './ToolCallContext';
import {
    PRETTY_PRINT_MAX_CHARS,
    ToolCallState,
    formatArguments,
    formatNumber,
    formatResult,
    hasExecutedResult,
    isStoredCapped,
    isTruncated,
} from './toolCalls';

const DECIDED_BY_KEYS = {
    AUTO_READONLY: 'tool-call.decided-by.auto-readonly',
    AUTO_YOLO: 'tool-call.decided-by.auto-yolo',
    APPROVED: 'tool-call.decided-by.approved',
    REJECTED: 'tool-call.decided-by.rejected',
} as const;

const resultLabelKey = (state: ToolCallState): string => {
    switch (state) {
        case 'succeeded':
            return 'tool-call.detail.result';
        case 'failed-notfound':
        case 'failed-error':
            return 'tool-call.detail.error';
        case 'rejected':
            return 'tool-call.detail.not-run';
        default:
            return 'tool-call.detail.not-run-yet';
    }
};

export type RawPayloadState =
    | { status: 'idle' }
    | { status: 'loading' }
    | { status: 'loaded'; payload: ToolCallPayload };

type ToolCallDetailProps = {
    call: ToolCall;
    state: ToolCallState;
    rawOpen: boolean;
    onToggleRaw: () => void;
    raw: RawPayloadState;
};

type RawView = 'full' | 'model';

const RawBlock = ({ call, payload }: { call: ToolCall; payload: ToolCallPayload }) => {
    const translate = useTranslate('components.copilot');
    const [view, setView] = useState<RawView>('full');
    const truncated = isTruncated(call);
    // Up to 64,000 characters parsed and formatted: once per payload, not per render
    const full = useMemo(() => formatResult(payload.result ?? '', call.resultChars), [payload.result, call.resultChars]);
    // The replayed string as PHP built it: never parsed - a middle-elided payload is not
    // JSON - and never sliced here, since PHP cuts at code points
    const showModel = truncated && view === 'model';

    return (
        <>
            {truncated && (
                <SegmentedControl<RawView>
                    className="plesk-copilot-tool-detail__views"
                    buttons={[
                        { value: 'full', title: String(translate('tool-call.view.full')) },
                        { value: 'model', title: String(translate('tool-call.view.model')) },
                    ]}
                    selected={view}
                    onChange={setView}
                />
            )}
            {!showModel && call.resultChars != null && call.resultChars > PRETTY_PRINT_MAX_CHARS && (
                <Hint component="div" className="plesk-copilot-tool-detail__note">{translate('tool-call.raw.not-formatted')}</Hint>
            )}
            <ConsoleOutput
                className="plesk-copilot-tool-detail__code plesk-copilot-tool-detail__code--raw"
                autoscroll={false}
                data-testid="copilot-tool-call-raw"
            >
                {showModel ? payload.modelResult ?? '' : full.text}
            </ConsoleOutput>
        </>
    );
};

const ToolCallDetail = ({ call, state, rawOpen, onToggleRaw, raw }: ToolCallDetailProps) => {
    const translate = useTranslate('components.copilot');
    const truncated = isTruncated(call);
    // A rejected call's stored tool row is the synthetic rejection sent to the model:
    // protocol, not a result
    const canShowRaw = hasExecutedResult(call);
    const failed = state === 'failed-error' || state === 'failed-notfound';

    let resultSize: string | null = null;
    if (truncated && call.resultChars != null && call.modelResultChars != null) {
        const head = Math.ceil(call.modelResultChars / 2);
        const tail = Math.floor(call.modelResultChars / 2);
        const params = { total: formatNumber(call.resultChars), head: formatNumber(head), tail: formatNumber(tail) };
        // "the first and last N", unless an odd count makes the two differ
        const key = `tool-call.result-size.${isStoredCapped(call) ? 'stored-and-model' : 'model'}${head === tail ? '' : '-uneven'}`;
        resultSize = String(translate(key, { ...params, stored: formatNumber(call.storedChars ?? 0) }));
    }

    return (
        <>
            <dl className="plesk-copilot-tool-detail__grid">
                <Hint component="dt">{translate('tool-call.detail.tool')}</Hint>
                <dd><code>{call.name}</code></dd>
                <Hint component="dt">{translate('tool-call.detail.decided-by')}</Hint>
                <dd>{translate(call.decision ? DECIDED_BY_KEYS[call.decision] : 'tool-call.decided-by.pending')}</dd>
                {resultSize && (
                    <>
                        <Hint component="dt">{translate('tool-call.detail.result-size')}</Hint>
                        <dd>{resultSize}</dd>
                    </>
                )}
            </dl>
            <div className="plesk-copilot-tool-detail__section">
                <Hint component="div" className="plesk-copilot-tool-detail__heading">{translate('tool-call.detail.arguments')}</Hint>
                <ConsoleOutput className="plesk-copilot-tool-detail__code" autoscroll={false}>{formatArguments(call.arguments)}</ConsoleOutput>
            </div>
            <div className="plesk-copilot-tool-detail__section">
                <Hint component="div" className="plesk-copilot-tool-detail__heading">
                    <span>{translate(resultLabelKey(state))}</span>
                    {canShowRaw && (
                        <button type="button" className="plesk-copilot-tool-detail__raw-toggle" aria-expanded={rawOpen} onClick={onToggleRaw}>
                            {translate(rawOpen ? 'tool-call.raw.hide' : 'tool-call.raw.show')}
                        </button>
                    )}
                </Hint>
                {canShowRaw && rawOpen && (
                    raw.status === 'loaded'
                        ? <RawBlock call={call} payload={raw.payload} />
                        : (
                            <ConsoleOutput className="plesk-copilot-tool-detail__code plesk-copilot-tool-detail__code--raw" autoscroll={false} aria-busy="true">
                                {'…'}
                            </ConsoleOutput>
                        )
                )}
                {failed && call.outcome && (
                    <ConsoleOutput className="plesk-copilot-tool-detail__code plesk-copilot-tool-detail__error" autoscroll={false}>
                        <Text intent={state === 'failed-error' ? 'danger' : 'muted'}>{call.outcome}</Text>
                    </ConsoleOutput>
                )}
            </div>
        </>
    );
};

export default ToolCallDetail;
